import React, { useEffect, useState } from 'react';
import { Box, Typography, List, ListItem, ListItemAvatar, Avatar, ListItemText, Chip, CircularProgress } from '@mui/material';
import { EventBusy as EventBusyIcon } from '@mui/icons-material';
import UserAvatar from '../../UserAvatar';
import { timeOffService } from '../../../services/timeOffService';
import { TimeOffRequest } from '../../../types';

const TeamAvailabilityWidget: React.FC = () => {
    const [absentUsers, setAbsentUsers] = useState<TimeOffRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchAvailability = async () => {
            try {
                const now = new Date();
                const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                const requests = await timeOffService.getTimeOffRequestsByDate(today);

                setAbsentUsers(requests.filter(req => req.status === 'approved'));
                setLoading(false);
            } catch (err) {
                console.error('Failed to fetch availability', err);
                setError('Failed to load team availability');
                setLoading(false);
            }
        };

        fetchAvailability();
    }, []);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>;
    }

    if (error) {
        return <Typography color="error" variant="body2">{error}</Typography>;
    }

    if (absentUsers.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Everyone is available today!
                </Typography>
            </Box>
        );
    }

    return (
        <List dense sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {absentUsers.map((req) => (
                <ListItem key={req.id}>
                    <ListItemAvatar>
                        <UserAvatar
                            name={req.userName}
                            src={req.profile_picture}
                            sx={{ width: 32, height: 32, fontSize: '0.8rem' }}
                        />
                    </ListItemAvatar>
                    <ListItemText
                        primary={req.userName}
                        secondary={req.holidayType === 'remote' ? 'Remote' : (req.halfDayPeriod ? `Half Day (${req.halfDayPeriod})` : 'On Leave')}
                    />
                    <Chip
                        label={req.holidayType === 'remote' ? 'Remote' : 'Out'}
                        color={req.holidayType === 'remote' ? 'info' : 'warning'}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.6rem' }}
                    />
                </ListItem>
            ))}
        </List>
    );
};

export default TeamAvailabilityWidget;
